import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  Row,
  Col,
  Button,
  Switch,
  Form,
  Input,
  Select,
  message,
  Space,
  Modal,
} from 'antd';
import {
  SettingOutlined,
  BellOutlined,
  LockOutlined,
  UserOutlined,
  GlobalOutlined,
  ExclamationCircleOutlined,
  SafetyOutlined,
  LogoutOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { userService, authService } from '../services/api';
import { useNavigate } from 'react-router-dom';
import './CustomerDashboard.css';

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;

const Settings = () => {
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [notificationSettings, setNotificationSettings] = useState({
    email_notifications: true,
    push_notifications: true,
    sms_notifications: false,
    order_notifications: true,
    promotional_emails: false,
  });
  const [privacySettings, setPrivacySettings] = useState({
    profile_visibility: 'public',
    show_phone: false,
    show_email: false,
    allow_marketing: false,
  });
  const [passwordForm] = Form.useForm();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showLangRegionModal, setShowLangRegionModal] = useState(false);
  const [langRegionForm] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserProfile();
    fetchNotificationSettings();
    fetchPrivacySettings();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await userService.getProfile();
      const data = response.data?.data || response.data;
      setUser(data);
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const fetchNotificationSettings = async () => {
    try {
      const response = await userService.getNotificationSettings();
      const data = response.data?.data || response.data;
      if (data) setNotificationSettings(data);
    } catch (error) {
      console.error('Error fetching notification settings:', error);
    }
  };

  const fetchPrivacySettings = async () => {
    try {
      const response = await userService.getPrivacySettings();
      const data = response.data?.data || response.data;
      if (data) setPrivacySettings(data);
    } catch (error) {
      console.error('Error fetching privacy settings:', error);
    }
  };

  const handleNotificationChange = async (key, value) => {
    const newSettings = { ...notificationSettings, [key]: value };
    setNotificationSettings(newSettings);
    try {
      await userService.updateNotificationSettings({ [key]: value });
      message.success('Notification settings updated');
    } catch (error) {
      setNotificationSettings(notificationSettings);
      message.error('Failed to update notification settings');
    }
  };

  const handlePrivacyChange = async (key, value) => {
    const newSettings = { ...privacySettings, [key]: value };
    setPrivacySettings(newSettings);
    try {
      await userService.updatePrivacySettings({ [key]: value });
      message.success('Privacy settings updated');
    } catch (error) {
      setPrivacySettings(privacySettings);
      message.error('Failed to update privacy settings');
    }
  };

  const handlePasswordChange = async (values) => {
    setLoading(true);
    try {
      await userService.changePassword(
        values.current_password,
        values.new_password,
        values.confirm_password,
      );
      message.success('Password changed successfully');
      setShowPasswordModal(false);
      passwordForm.resetFields();
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to change password';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleLangRegionSave = async (values) => {
    try {
      await userService.updateProfile({ language: values.language, region: values.region });
      setUser((prev) => ({ ...prev, language: values.language, region: values.region }));
      message.success('Language & Region updated');
      setShowLangRegionModal(false);
    } catch (error) {
      message.error('Failed to update settings');
    }
  };

  const handleLogout = () => {
    Modal.confirm({
      title: 'Confirm Logout',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to logout?',
      okText: 'Logout',
      cancelText: 'Cancel',
      onOk: () => {
        authService.logout();
      },
    });
  };

  const handleDeleteAccount = () => {
    Modal.confirm({
      title: 'Delete Account',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone. Are you sure you want to delete your account?',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        message.info('Account deletion feature coming soon');
      },
    });
  };

  if (!user) {
    return (
      <div className="customer-page" style={{ padding: '0' }}>
        <div className="customer-page-header">
          <div
            className="customer-card-loading"
            style={{ height: 24, width: 200, marginBottom: 8 }}
          />
          <div className="customer-card-loading" style={{ height: 16, width: 300 }} />
        </div>
      </div>
    );
  }

  return (
    <div className="customer-page" style={{ padding: '0' }}>
      <div className="customer-page-header">
        <Title level={2}>Account Settings</Title>
        <Paragraph>Manage your account preferences, notifications, and privacy settings.</Paragraph>
      </div>

      <Row gutter={[24, 24]}>
        {/* Account & Security */}
        <Col xs={24} md={12}>
          <Card
            className="customer-card animate-fade-slide-up animate-stagger-1"
            title={
              <>
                <LockOutlined style={{ marginRight: 8 }} /> Account &amp; Security
              </>
            }
          >
            <Paragraph style={{ color: '#6b7280', marginBottom: 20 }}>
              Update your personal details on the Profile page, or change your account password
              here.
            </Paragraph>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <Button
                className="customer-btn-secondary"
                icon={<UserOutlined />}
                block
                style={{ height: 42, textAlign: 'left', justifyContent: 'flex-start' }}
                onClick={() => navigate('/profile')}
              >
                Edit Profile Details
              </Button>
              <Button
                className="customer-btn-primary"
                icon={<LockOutlined />}
                block
                style={{ height: 42, textAlign: 'left', justifyContent: 'flex-start' }}
                onClick={() => setShowPasswordModal(true)}
              >
                Change Password
              </Button>
            </Space>
          </Card>
        </Col>

        {/* Notification Settings */}
        <Col xs={24} md={12}>
          <Card
            className="customer-card animate-fade-slide-up animate-stagger-2"
            title={
              <>
                <BellOutlined style={{ marginRight: 8 }} /> Notifications
              </>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '4px 0',
                }}
              >
                <Text style={{ color: '#374151' }}>Email Notifications</Text>
                <Switch
                  checked={notificationSettings.email_notifications}
                  onChange={(checked) => handleNotificationChange('email_notifications', checked)}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '4px 0',
                }}
              >
                <Text style={{ color: '#374151' }}>Push Notifications</Text>
                <Switch
                  checked={notificationSettings.push_notifications}
                  onChange={(checked) => handleNotificationChange('push_notifications', checked)}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '4px 0',
                }}
              >
                <Text style={{ color: '#374151' }}>SMS Notifications</Text>
                <Switch
                  checked={notificationSettings.sms_notifications}
                  onChange={(checked) => handleNotificationChange('sms_notifications', checked)}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '4px 0',
                }}
              >
                <Text style={{ color: '#374151' }}>Order Updates</Text>
                <Switch
                  checked={notificationSettings.order_notifications}
                  onChange={(checked) => handleNotificationChange('order_notifications', checked)}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '4px 0',
                }}
              >
                <Text style={{ color: '#374151' }}>Promotional Emails</Text>
                <Switch
                  checked={notificationSettings.promotional_emails}
                  onChange={(checked) => handleNotificationChange('promotional_emails', checked)}
                />
              </div>
            </Space>
          </Card>
        </Col>

        {/* Privacy Settings */}
        <Col xs={24} md={12}>
          <Card
            className="customer-card animate-fade-slide-up animate-stagger-3"
            title={
              <>
                <SafetyOutlined style={{ marginRight: 8 }} /> Privacy
              </>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <Text strong style={{ color: '#2d5a3d' }}>
                  Profile Visibility
                </Text>
                <Select
                  value={privacySettings.profile_visibility}
                  onChange={(value) => handlePrivacyChange('profile_visibility', value)}
                  style={{ width: '100%', marginTop: 8, borderRadius: 10 }}
                >
                  <Option value="public">Public</Option>
                  <Option value="friends">Friends Only</Option>
                  <Option value="private">Private</Option>
                </Select>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '4px 0',
                }}
              >
                <Text style={{ color: '#374151' }}>Show Phone Number</Text>
                <Switch
                  checked={privacySettings.show_phone}
                  onChange={(checked) => handlePrivacyChange('show_phone', checked)}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '4px 0',
                }}
              >
                <Text style={{ color: '#374151' }}>Show Email Address</Text>
                <Switch
                  checked={privacySettings.show_email}
                  onChange={(checked) => handlePrivacyChange('show_email', checked)}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '4px 0',
                }}
              >
                <Text style={{ color: '#374151' }}>Allow Marketing</Text>
                <Switch
                  checked={privacySettings.allow_marketing}
                  onChange={(checked) => handlePrivacyChange('allow_marketing', checked)}
                />
              </div>
            </Space>
          </Card>
        </Col>

        {/* Account Actions */}
        <Col xs={24} md={12}>
          <Card
            className="customer-card animate-fade-slide-up animate-stagger-4"
            title="Account Actions"
          >
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <Button
                className="customer-btn-secondary"
                icon={<GlobalOutlined />}
                block
                style={{ height: 42, textAlign: 'left', justifyContent: 'flex-start' }}
                onClick={() => setShowLangRegionModal(true)}
              >
                Language & Region
              </Button>
              <Button
                className="customer-btn-secondary"
                icon={<SettingOutlined />}
                block
                style={{ height: 42, textAlign: 'left', justifyContent: 'flex-start' }}
                onClick={() => navigate('/notifications')}
              >
                View Notifications
              </Button>
              <Button
                className="customer-btn-secondary"
                icon={<LogoutOutlined />}
                block
                style={{ height: 42, textAlign: 'left', justifyContent: 'flex-start' }}
                onClick={handleLogout}
              >
                Logout
              </Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                block
                style={{
                  height: 42,
                  borderRadius: 10,
                  textAlign: 'left',
                  justifyContent: 'flex-start',
                }}
                onClick={handleDeleteAccount}
              >
                Delete Account
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Password Change Modal */}
      <Modal
        className="customer-modal"
        title="Change Password"
        open={showPasswordModal}
        onCancel={() => setShowPasswordModal(false)}
        footer={null}
      >
        <Form form={passwordForm} layout="vertical" onFinish={handlePasswordChange}>
          <Form.Item
            name="current_password"
            label="Current Password"
            rules={[{ required: true, message: 'Please enter your current password' }]}
          >
            <Input.Password prefix={<LockOutlined />} style={{ borderRadius: 10 }} />
          </Form.Item>
          <Form.Item
            name="new_password"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter your new password' },
              { min: 6, message: 'Password must be at least 6 characters' },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} style={{ borderRadius: 10 }} />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label="Confirm New Password"
            dependencies={['new_password']}
            rules={[
              { required: true, message: 'Please confirm your new password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} style={{ borderRadius: 10 }} />
          </Form.Item>
          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                className="customer-btn-secondary"
                onClick={() => setShowPasswordModal(false)}
              >
                Cancel
              </Button>
              <Button className="customer-btn-primary" htmlType="submit" loading={loading}>
                Change Password
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Language & Region Modal */}
      <Modal
        className="customer-modal"
        title="Language & Region"
        open={showLangRegionModal}
        onCancel={() => setShowLangRegionModal(false)}
        footer={null}
      >
        <Form
          form={langRegionForm}
          layout="vertical"
          onFinish={handleLangRegionSave}
          initialValues={{ language: user.language || 'en', region: user.region || 'np' }}
        >
          <Form.Item name="language" label="Language" rules={[{ required: true }]}>
            <Select style={{ borderRadius: 10 }}>
              <Option value="en">English</Option>
              <Option value="ne">नेपाली</Option>
            </Select>
          </Form.Item>
          <Form.Item name="region" label="Region" rules={[{ required: true }]}>
            <Select style={{ borderRadius: 10 }}>
              <Option value="np">Nepal</Option>
              <Option value="in">India</Option>
              <Option value="us">United States</Option>
              <Option value="uk">United Kingdom</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                className="customer-btn-secondary"
                onClick={() => setShowLangRegionModal(false)}
              >
                Cancel
              </Button>
              <Button className="customer-btn-primary" htmlType="submit">
                Save
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Settings;
